import React, { useState, useMemo, useEffect, memo } from 'react';

import { TRADE_CONSTANTS } from 'actionTypes/trade';
import { isEmpty } from 'helpers/utils';
import { getTopGainersSelector } from 'selectors/marketsCards';
import { productsSelector } from 'selectors/tradeSelectors';
import { useAppDispatch, useAppSelector } from 'storeHooks';
import { AngleLeft as AngleLeftIcon } from 'styles/assets/icons';
import Box from 'UIKit/Box';
import DeltaButton from 'UIKit/DeltaButton';

import TopSectionSkeletonLoader from '../TopSectionCards/TopSectionSkeletonLoader';
import { filterTopGainersByStep } from './helper';
import MarketTopCoinsOverviewCard from './MarketTopCoinsOverviewCard';
import classes from './style.module.scss';

const AngleRightIcon = () => <AngleLeftIcon className={classes.rightIcon} />;

const CombinedTopCoinsOverviewCards = memo(() => {
  const dispatch = useAppDispatch();
  const products = useAppSelector(productsSelector);
  const topCoinsData = useAppSelector(getTopGainersSelector);
  const [currentOverviewCard, setCurrentOverviewCard] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    dispatch({ type: TRADE_CONSTANTS.POLL_TOP_GAINERS_LOSERS });

    return () => {
      dispatch({ type: TRADE_CONSTANTS.KILL_TOP_GAINERS_LOSERS_TIMER });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (topCoinsData && products) {
      setIsLoading(false);
    }
  }, [topCoinsData, products]);

  // Calculate total pages
  const dataLength = topCoinsData?.length || 0;
  const ITEMS_PER_STEP = 4;
  const TOP_COINS_STEPS_LIMIT = 3;
  const totalPages = Math.min(
    Math.ceil(dataLength / ITEMS_PER_STEP),
    TOP_COINS_STEPS_LIMIT
  );

  // Handle pagination
  const switchMarketsOverviewCard = direction => {
    setCurrentOverviewCard(prevCard =>
      direction === 'left'
        ? Math.max(1, prevCard - 1)
        : Math.min(totalPages, prevCard + 1)
    );
  };

  // Memoize filteredTopGainers to avoid recalculating on every render
  const filteredTopCoins = useMemo(
    () => filterTopGainersByStep(currentOverviewCard, topCoinsData),
    [currentOverviewCard, topCoinsData]
  );

  if (isLoading) {
    return <TopSectionSkeletonLoader count={1} />;
  }

  if (isEmpty(topCoinsData) || isEmpty(products)) return null;

  return (
    <Box className={classes.container} data-testid="combinedTopCoinsCards">
      {totalPages > 1 && (
        <Box className={classes.switchBtnWrapper}>
          <DeltaButton
            onClick={() => switchMarketsOverviewCard('left')}
            disabled={currentOverviewCard === 1}
            data-testid="SwitchMarketsOverViewCardsLeftButton"
            className={classes.btn}
            icon={<AngleLeftIcon />}
          />
          <DeltaButton
            onClick={() => switchMarketsOverviewCard('right')}
            disabled={currentOverviewCard === totalPages}
            data-testid="SwitchMarketsOverViewCardsRightButton"
            className={classes.btn}
            icon={<AngleRightIcon />}
          />
        </Box>
      )}
      <MarketTopCoinsOverviewCard
        topCoinsData={filteredTopCoins}
        currentOverviewCard={currentOverviewCard}
      />
    </Box>
  );
});

export default CombinedTopCoinsOverviewCards;
